import {
  AlertTriangle,
  Check,
  Loader,
  Pause,
  XCircle,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  type EffectiveSessionState,
  type SessionInfo,
  ageSeconds,
  deriveSessionState,
} from "@/api/types";

/**
 * The status of a pending client action, on top of server session state.
 * The server doesn't know about these — they exist between "user hit send"
 * and "agent produced its first event".
 */
export type ClientPhase =
  | "idle"
  | "waking_desk" // wake mutation in flight
  | "sending" // start/send mutation in flight
  | "awaiting_first_event"; // mutation succeeded but agent hasn't emitted yet

interface SessionStatusProps {
  session: SessionInfo | null;
  clientPhase: ClientPhase;
  eventCount: number | null;
  compact?: boolean;
}

const STATE_META: Record<
  EffectiveSessionState,
  {
    label: string;
    color: string;
    bg: string;
    icon: (size: number) => React.ReactNode;
    pulse?: boolean;
  }
> = {
  starting: {
    label: "Starting",
    color: "var(--accent)",
    bg: "color-mix(in srgb, var(--accent) 10%, transparent)",
    icon: (s) => (
      <Loader size={s} style={{ animation: "spin 1s linear infinite" }} />
    ),
  },
  running: {
    label: "Thinking",
    color: "var(--info)",
    bg: "color-mix(in srgb, var(--info) 10%, transparent)",
    icon: (s) => <Zap size={s} />,
    pulse: true,
  },
  thinking: {
    label: "Thinking hard",
    color: "var(--info)",
    bg: "color-mix(in srgb, var(--info) 12%, transparent)",
    icon: (s) => <Zap size={s} />,
    pulse: true,
  },
  stalled: {
    label: "Stalled",
    color: "var(--warm)",
    bg: "color-mix(in srgb, var(--warm) 12%, transparent)",
    icon: (s) => <AlertTriangle size={s} />,
  },
  completed: {
    label: "Done",
    color: "var(--ok)",
    bg: "color-mix(in srgb, var(--ok) 10%, transparent)",
    icon: (s) => <Check size={s} />,
  },
  failed: {
    label: "Failed",
    color: "var(--err)",
    bg: "color-mix(in srgb, var(--err) 10%, transparent)",
    icon: (s) => <XCircle size={s} />,
  },
  interrupted: {
    label: "Stopped",
    color: "var(--warm)",
    bg: "color-mix(in srgb, var(--warm) 10%, transparent)",
    icon: (s) => <Pause size={s} />,
  },
};

/**
 * Renders the prominent status line for a session tile. This is the single
 * source of truth for "what's happening right now" — combines server-side
 * session state with liveness signals (last activity, heartbeat) and
 * client-side phase (pending mutations).
 *
 * Never leaves the user wondering whether the agent is live or hung.
 */
export function SessionStatus({
  session,
  clientPhase,
  eventCount,
  compact,
}: SessionStatusProps) {
  // Heartbeat ticker so "12s ago" updates without waiting for a poll
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Client phase takes precedence when there's no session yet, or when
  // the mutation is in flight. Once we have events flowing, server state wins.
  if (clientPhase === "waking_desk") {
    return (
      <StatusPill
        label="Waking desk"
        sub="sprite is warming up…"
        color="var(--warm)"
        bg="color-mix(in srgb, var(--warm) 12%, transparent)"
        icon={
          <Loader size={12} style={{ animation: "spin 1s linear infinite" }} />
        }
        compact={compact}
      />
    );
  }

  if (clientPhase === "sending") {
    return (
      <StatusPill
        label="Sending"
        sub="your message is on its way"
        color="var(--accent)"
        bg="color-mix(in srgb, var(--accent) 12%, transparent)"
        icon={
          <Loader size={12} style={{ animation: "spin 1s linear infinite" }} />
        }
        compact={compact}
      />
    );
  }

  if (clientPhase === "awaiting_first_event") {
    return (
      <StatusPill
        label="Agent starting"
        sub="waiting for first response…"
        color="var(--accent)"
        bg="color-mix(in srgb, var(--accent) 12%, transparent)"
        icon={
          <Loader size={12} style={{ animation: "spin 1s linear infinite" }} />
        }
        pulse
        compact={compact}
      />
    );
  }

  if (!session) {
    return (
      <StatusPill
        label="Ready"
        sub="send a message to start"
        color="var(--t3)"
        bg="transparent"
        icon={<Zap size={12} />}
        compact={compact}
      />
    );
  }

  const state = deriveSessionState(session);
  const meta = STATE_META[state];
  const activityAge = ageSeconds(session.lastActivityAt);

  // Build a sub-line that answers "is it alive?" honestly
  let sub = "";
  if (state === "running") {
    sub = activityAge != null ? `active ${activityAge}s ago` : "active";
  } else if (state === "thinking") {
    sub = activityAge != null ? `quiet for ${fmtAge(activityAge)}` : "quiet";
  } else if (state === "stalled") {
    if (!session.managerAlive) {
      sub = "heartbeat lost — process may have died";
    } else if (activityAge != null) {
      sub = `no activity for ${fmtAge(activityAge)}`;
    } else {
      sub = "no recent activity";
    }
  } else if (state === "starting") {
    sub = activityAge != null ? `launching · ${activityAge}s` : "launching…";
  } else if (state === "completed") {
    sub = `${eventCount ?? session.eventCount} events`;
  } else if (state === "failed") {
    sub = "agent returned an error";
  } else if (state === "interrupted") {
    sub = "you stopped the agent";
  }

  return (
    <StatusPill
      label={meta.label}
      sub={sub}
      color={meta.color}
      bg={meta.bg}
      icon={meta.icon(12)}
      pulse={meta.pulse}
      compact={compact}
    />
  );
}

function StatusPill({
  label,
  sub,
  color,
  bg,
  icon,
  pulse,
  compact,
}: {
  label: string;
  sub?: string;
  color: string;
  bg: string;
  icon: React.ReactNode;
  pulse?: boolean;
  compact?: boolean;
}) {
  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1 rounded"
      style={{ background: bg, color, flexShrink: 0 }}
    >
      <span
        className="flex items-center justify-center shrink-0"
        style={{
          animation: pulse ? "pulse 1.6s ease-in-out infinite" : undefined,
        }}
      >
        {icon}
      </span>
      <span
        className="text-[11px] font-medium uppercase tracking-wider"
        style={{ letterSpacing: "0.06em" }}
      >
        {label}
      </span>
      {sub && !compact && (
        <span
          className="text-[11px] truncate"
          style={{
            color: "var(--t3)",
            fontFamily: "var(--font-mono)",
            opacity: 0.9,
          }}
        >
          {sub}
        </span>
      )}
    </div>
  );
}

function fmtAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}
